import React, {useEffect, useState} from 'react';
import axios from "axios";
import config from '../../config';
import './Quiz.css';
import {useTranslation} from "react-i18next";
import scrollToTop from "../../scripts/scroll-to-top";

const SECONDS_FOR_QUESTION = 15;

function Quiz(props) {
    const { t, i18n } = useTranslation();

    const [fields, setFields] = useState(null);
    const [activeStep, setActiveStep] = useState(0);
    const [amountOfFieldsSets, setAmountOfFieldsSets] = useState(0);
    const [answers, setAnswers] = useState([]);
    const [progress, setProgress] = useState(0);
    const [answersSetSize, setAnswersSetSize] = useState(1);
    const [srcFields, setSrcFields] = useState([]);
    const [errorMessage, setErrorMessage] = useState(null);
    const [estimatedTime, setEstimatedTime] = useState(0);

    const getPageContent = async (answersSetSize) => {
        try {
            const response = await axios.post(
                config.API_URL_FOR_LOCAL_DEVELOPMENT + '/api/v1/quiz-climacteric/questions',
                {
                    language: config.LANGUAGE || 'en',
                }
            );

            const srcFields = response.data.fields
            const arrayOfObjects = objToArr(srcFields);
            setSrcFields(arrayOfObjects);
            const filteredArr = arrayOfObjects.filter(item => item.type === 'enum');

            setFields(splitArrayIntoGroups(filteredArr, answersSetSize));
            setAnswersSetSize(answersSetSize);
            setEstimatedTime(filteredArr.length * SECONDS_FOR_QUESTION);
        } catch (error) {
            console.error('Error while retrieving data:', error);
        }
    }

    const objToArr = (srcFields) => {
        return Object.keys(srcFields).map((key) => ({
            key,
            ...srcFields[key]
        }))
    }

    const splitArrayIntoGroups = (array, groupSize) => {
        const newArray = [];

        for (let i = 0; i < array.length; i += groupSize) {
            const subArray = array.slice(i, i + groupSize);
            newArray.push(subArray);
            setAmountOfFieldsSets((prevState) => prevState + 1)
        }

        return newArray;
    }

    const handleNext = () => {
        if (activeStep + 1 === amountOfFieldsSets) {
            finishTest();
            return;
        }

        scrollToTop();
        setActiveStep((prevStep) => prevStep + 1);
    };

    const handleBack = () => {
        if (activeStep === 0) {
            props.setNewCurrentStep(1);
            return;
        }

        setActiveStep((prevStep) => prevStep - 1);
    };

    const finishTest = () => {
        if (answers.length === answersSetSize * amountOfFieldsSets) {
            setErrorMessage(null);
            props.setAnswers(answers);
            props.setNewCurrentStep(3);
        } else {
            setErrorMessage(t('Complete the required field'));
        }
    }

    const handleChange = (key, value, label, valueContent) => {
        const answerAlreadySaved = answers.find(item => item.key === key)
        const answersCopy = JSON.parse(JSON.stringify(answers));

        if (answerAlreadySaved) {
            const indexOfAnswer = answers.findIndex(item => item.key === key)

            answersCopy[indexOfAnswer].value = value;
            setAnswers(answersCopy);
        } else {
            answersCopy.push({ key, value, label, valueContent });
            setAnswers(answersCopy);
            setEstimatedTime(prevState => prevState - SECONDS_FOR_QUESTION);
        }

        calcProgress(answersCopy.length);
    }

    const isChecked = (key, value) => {
        const indexOfAnswer = answers.findIndex(answer => answer.key === key);

        if (indexOfAnswer !== -1) {
            return answers[indexOfAnswer]?.value === value;
        } else {
            return false;
        }
    }

    const calcProgress = (amountOfAnsweredAnswers) => {
        const totalAmountOfAnswers = answersSetSize * amountOfFieldsSets;
        const progress = (amountOfAnsweredAnswers * 100) / totalAmountOfAnswers;

        setProgress(Math.round(progress));
    }

    const getQuestionIndex = (label) => {
        return srcFields.findIndex(item => item.label === label);
    }

    const convertToFormat = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return minutes + " m " + remainingSeconds + " s";
    }

    useEffect(  () => {
        getPageContent(props.isMobileScreen ? 1 : 3);
    }, [props.isMobileScreen])

    useEffect(() => {
        scrollToTop();
    }, [])

    if (fields) {
        return (
            <div className="Quiz">
                <div className='progress-line-box d-flex align-items-center p-0 px-md-3 mb-4'>
                    <span className='progress-line-label d-none d-md-block label-1 mr-25 text-nowrap'>{t('Progress Tracker')}</span>

                    <div className='progress-line flex-grow-1 w-100'>
                        <div
                            className='progress-line__body'
                            style={{ width: progress + '%' }}
                        ></div>
                    </div>

                    <span className='progress-line-label d-none d-md-block text-nowrap ml-25'>{t('Estimated Time')}: {convertToFormat(estimatedTime)}</span>
                </div>

                <h1 className='title-1 mb-2'>{t("Your Questions")}</h1>

                {fields.map((fieldsSet, index) => (
                    fieldsSet.map((field, indexOfSet) => {
                        if (activeStep === index) {
                            if (!props.isMobileScreen) {
                                return (
                                    <div
                                        className='box-1 mb-2'
                                        key={indexOfSet}
                                    >
                                        <div className='label-1 mb-2'>
                                            {getQuestionIndex(field.label) + 1 + '. ' + field.label}
                                        </div>

                                        <div className='d-block d-md-flex flex-wrap'>
                                            {
                                                field.options && answers && Object.entries(field.options).map(([key, value]) => ({ key, value }))
                                                    .map((option, index) => (
                                                        <label
                                                            className='form-label d-flex mb-2'
                                                            key={index}
                                                        >
                                                            <input
                                                                className='d-none'
                                                                type="radio"
                                                                name={field.key}
                                                                checked={isChecked(field.key, option.key)}
                                                                onChange={(e) => handleChange(field.key, option.key, field.label, option.value)}
                                                            />

                                                            <div
                                                                className={isChecked(field.key, option.key) ? 'checkbox-1 checkbox-1--checked mr-15' : 'checkbox-1 mr-15'}
                                                                role='button'
                                                            ></div>

                                                            <span role='button'>{option.value}</span>
                                                        </label>
                                                    ))
                                            }
                                        </div>
                                    </div>
                                );
                            } else {
                                return (
                                    <div
                                        className='mb-2'
                                        key={indexOfSet}
                                    >
                                        <div className='label-1 mb-3'>
                                            {getQuestionIndex(field.label) + 1 + '. ' + field.label}
                                        </div>

                                        <div className='d-block d-md-flex flex-wrap'>
                                            {
                                                field.options && answers && Object.entries(field.options).map(([key, value]) => ({ key, value }))
                                                    .map((option, index) => (
                                                        <label
                                                            className='d-flex mb-15'
                                                            key={index}
                                                        >
                                                            <input
                                                                className='d-none'
                                                                type="radio"
                                                                name={field.key}
                                                                checked={isChecked(field.key, option.key)}
                                                                onChange={(e) => handleChange(field.key, option.key, field.label, option.value)}
                                                            />

                                                            <div
                                                                className={isChecked(field.key, option.key) ? 'checkbox-1 checkbox-1--checked mr-15' : 'checkbox-1 mr-15'}
                                                                role='button'
                                                            ></div>

                                                            <span role='button'>{option.value}</span>
                                                        </label>
                                                    ))
                                            }
                                        </div>
                                    </div>
                                )
                            }
                        }
                        return null;
                    })
                ))}

                <footer className='mt-5'>
                    {
                        errorMessage &&
                        <div className='form-error mb-15 text-center'>{errorMessage}</div>
                    }

                    <div className='d-flex justify-content-center align-items-center'>
                        <button
                            className='button-1 mr-6'
                            onClick={() => handleBack()}
                        >
                            {t("Back Button")}
                        </button>

                        <button
                            className='button-1'
                            onClick={() => handleNext()}
                        >
                            {t("Continue Button")}
                        </button>
                    </div>
                </footer>
            </div>
        );
    } else {
        return (
            <div className='submitting-overlay-container position-relative'>
                <div className="submitting-overlay"></div>
            </div>
        )
    }
}

export default Quiz;
